/**
 * The gate every session screen sits behind: read the session, then apply the
 * status routing rule **once**.
 *
 * Two things live here so they cannot be re-decided per screen:
 *
 * **The session read.** `GET …/sessions/{id}` is the only source. Nothing about a
 * session is inferred from the list a manager tapped, because the list may be
 * minutes old and the session may have been posted from another iPad since.
 *
 * **The routing rule.** A deep link — a bookmark, a resumed URL, a link in a
 * message — is not a promise that the session is still in the state the URL
 * implies. So the route the session's *current* status affords is computed with
 * the same function S2's buttons use, and a URL that disagrees is redirected
 * rather than rendered. That is what makes "a session in `Posting` opens straight
 * onto S7" true of the URL as well as of the button, and it is why a manager who
 * bookmarked an entry URL for a count that has since failed lands on the review
 * screen with Business Central's error in front of them instead of on an entry
 * screen that would refuse every keystroke.
 *
 * The redirect cannot loop: `affordanceFor` is a pure function of status and
 * ownership, and each route it names is a fixed point of it.
 */
import { useEffect, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { useCountAreas, useSession } from "../api/queries";
import { Verifying } from "./Verifying";
import type { CountSession } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { clearPostAttempt } from "../offline/draftStore";
import { isTerminal, needsVerifying } from "../offline/reconcile";
import { useSessionRecord } from "../offline/useSessionRecord";
import { allowedRoutes, isMine, sessionPath, type SessionRoute as RouteName } from "../sessions/rules";

export interface SessionScreenProps {
  session: CountSession;
  /** Whether the signed-in principal is this session's responsible manager. */
  mine: boolean;
  areaId: string;
  /**
   * When a post was attempted from this device and no terminal outcome is
   * recorded — the marker that forces the verifying state (§ 4.5).
   */
  postAttemptedAt: string | null;
  /**
   * The UPN the **server** resolved, not the one the bundle believes.
   *
   * Passed down rather than re-read per screen because D4's flush gate needs it:
   * a draft held for a session somebody else owns must never be sent, and that
   * comparison has to be against the same identity `affordanceFor` used, or a
   * screen could offer entry while the flush refused it.
   */
  principal: string;
}

export function SessionRoute({
  expected,
  render,
}: {
  expected: RouteName;
  render: (props: SessionScreenProps) => ReactNode;
}) {
  const { areaId = "", id: sessionId = "" } = useParams();
  const { signedIn } = useAuth();
  const areas = useCountAreas(signedIn);
  const principal = areas.data?.principal ?? null;
  const enabled = signedIn && Boolean(areaId) && Boolean(sessionId);
  const { data: session, isLoading, error } = useSession(areaId, sessionId, enabled);
  const local = useSessionRecord(sessionId);

  // **Clear the post marker the moment Business Central gives a terminal answer**
  // (§ 4.5, rule 5), here rather than on a screen, because every screen reads the
  // routing decision this file makes and only this file makes it once per session.
  //
  // D6 found why it cannot be skipped: a failed post routes to S5, the manager
  // submits for review again, and the session goes back to `InReview`. A marker left
  // behind from the first attempt would then make `needsVerifying` true forever, and
  // the manager would be sent to a verifying screen for a post that never happened —
  // with no way to reach the confirm at all.
  const markerSet = Boolean(local.record?.post_attempted_at);
  const resolved = Boolean(session && isTerminal(session));
  useEffect(() => {
    if (markerSet && resolved) void clearPostAttempt(sessionId);
  }, [markerSet, resolved, sessionId]);

  if (!signedIn) {
    return (
      <section className="card">
        <h2>Not signed in</h2>
        <p>
          <Link to="/">Sign in</Link> to open this count.
        </p>
        <p className="muted small">
          The URL is kept — signing in and coming back to it will work.
        </p>
      </section>
    );
  }

  if (isLoading || areas.isLoading) {
    return <p className="muted">Reading this count from Business Central…</p>;
  }

  if (error) {
    return (
      <section className="card error">
        <h2>Could not open this count</h2>
        <p>{(error as Error).message}</p>
        <p className="muted">
          Session ids are not authorisations: a count belonging to another area
          reads as 404 through this one, and an area that is not yours reads as
          403. Both of those sentences come from the server, not from this screen.
        </p>
        <p>
          <Link to={`/areas/${encodeURIComponent(areaId)}`}>Back to this area's counts</Link>
        </p>
      </section>
    );
  }

  if (!session) {
    return <Navigate to={`/areas/${encodeURIComponent(areaId)}`} replace />;
  }

  // Wait for the principal before deciding anything about ownership. Treating a
  // not-yet-known identity as "not the owner" would send a manager to the
  // read-only view of their own count for as long as the areas read takes.
  if (!principal) {
    if (areas.error) {
      return (
        <section className="card error">
          <h2>Cannot tell who you are</h2>
          <p>{(areas.error as Error).message}</p>
          <p className="muted">
            Whether this count is yours is decided by comparing its responsible
            manager against the identity the <em>server</em> resolved, so this
            screen will not guess at it.
          </p>
        </section>
      );
    }
    return <p className="muted">Reading who you are signed in as…</p>;
  }

  // Wait for the local record before routing: a session with `post_attempted_at`
  // set must land on verifying, and rendering entry for the moment it takes
  // IndexedDB to answer would flash a Post-able screen at a count that may already
  // have posted.
  if (!local.loaded) {
    return <p className="muted">Checking what this device knows about this count…</p>;
  }

  // The marker beats the URL *and* beats the status affordance. § 4.5, rule 4: a
  // post was attempted and BC has not given a terminal outcome, so the only honest
  // screen is the one that asks BC and offers no way to send anything.
  const verifying = needsVerifying(session, local.record);

  // Redirect only when this state does not PERMIT the requested screen — not merely
  // when the screen differs from the default. A manager tapping "Go to review" on
  // their own open count is asking for something available to them; bouncing them
  // back to entry made S5 unreachable until D5 found it.
  const permitted = verifying ? (["posting"] as RouteName[]) : allowedRoutes(session, principal);
  if (!permitted.includes(expected)) {
    return <Navigate to={sessionPath(areaId, session, permitted[0]!)} replace />;
  }

  const props = {
    session,
    mine: isMine(session, principal),
    areaId,
    principal,
    postAttemptedAt: local.record?.post_attempted_at ?? null,
  };

  if (verifying) return <Verifying {...props} postAttemptedAt={props.postAttemptedAt} />;

  return <>{render(props)}</>;
}
